import store, { borrowerActions } from '@apply/adminStore';
import { useBorrowerEntityQuery, useRefreshBorrowerEntity } from '../../queries/borrowerEntityQueries';
import { BorrowerEntitySearchField } from './components/BorrowerEntitySearchField';
import { ClearBorrowerEntityButton } from './components/ClearBorrowerEntityButton';
import { AddBorrowerEntityButton } from './components/AddBorrowerEntityButton';
import { BorrowerEntityCard } from './components/BorrowerEntityCard/BorrowerEntityCard';
import '../../styles/_inputAlignedActions.scss';

interface BorrowerEntityProps {
    borrowerEntityId: number | undefined;
    onSelect: (borrowerEntityId: number | undefined) => void;
    disabled: boolean;
    required: boolean;
    /** Shows the buttons to add a new entity and edit the selected one. */
    canEdit: boolean;
}

/**
 * "Borrower Only": the borrower entity search, and the chosen entity's details below it. New entities are added, and the
 * selected one edited, in the borrower EditEntityModal, which the page hosting this must render (BorrowerModals).
 */
export const BorrowerEntity = ({ borrowerEntityId, onSelect, disabled, required, canEdit }: BorrowerEntityProps) => {
    const { data: entity } = useBorrowerEntityQuery(borrowerEntityId);
    const refreshBorrowerEntity = useRefreshBorrowerEntity();

    // The modal hands back the saved entity: refetch it so the card shows the changes, then select it
    const afterEntitySave = async (savedEntity: { entityId: number }) => {
        await refreshBorrowerEntity(savedEntity.entityId);
        onSelect(savedEntity.entityId);
    };

    // entityId 0 adds a new entity
    const openEntityModal = (entityId: number) =>
        store.dispatch(
            borrowerActions.setEditEntityModal({
                editEntityModal: { isOpen: true, entityId, onChange: afterEntitySave },
            })
        );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    <BorrowerEntitySearchField
                        value={borrowerEntityId}
                        onChange={onSelect}
                        disabled={disabled}
                        required={required}
                    />
                </div>
                <div
                    className='borrowerSelectorInputAlignedActions'
                    style={{ gap: '8px' }}
                >
                    <ClearBorrowerEntityButton
                        borrowerEntityId={borrowerEntityId}
                        onClear={() => onSelect(undefined)}
                        disabled={disabled}
                    />
                    {canEdit && (
                        <AddBorrowerEntityButton
                            onClick={() => openEntityModal(0)}
                            disabled={disabled}
                        />
                    )}
                </div>
            </div>
            {entity && (
                <BorrowerEntityCard
                    entity={entity}
                    onEdit={canEdit ? () => openEntityModal(entity.entityId) : undefined}
                />
            )}
        </div>
    );
};
